// src/components/reports/ReportActions.js

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useReportContext } from '../../contexts/ReportContext';

/**
 * Component providing action buttons for report export/share functionality
 */
const ReportActions = () => {
  const { t } = useTranslation('reports');
  const { 
    reportData, 
    isGeneratingReport, 
    exportCSV, 
    exportPDF, 
    shareReport 
  } = useReportContext();
  
  // Local loading states for actions
  const [isExportingCSV, setIsExportingCSV] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  
  // Handle CSV export with loading state
  const handleExportCSV = async () => {
    setIsExportingCSV(true);
    try {
      await exportCSV();
    } finally {
      setIsExportingCSV(false);
    }
  };
  
  // Handle PDF export with loading state
  const handleExportPDF = async () => {
    setIsExportingPDF(true);
    try {
      await exportPDF();
    } finally {
      setIsExportingPDF(false);
    }
  };
  
  // Handle share with loading state
  const handleShare = async () => {
    setIsSharing(true);
    try {
      await shareReport();
    } finally {
      setIsSharing(false);
    }
  };
  
  // Disable actions if no report data or if report is being generated
  const isDisabled = reportData.length === 0 || isGeneratingReport;
  
  return (
    <div className="report-actions">
      <div className="action-buttons">
        <button 
          className="action-button csv-button"
          onClick={handleExportCSV}
          disabled={isDisabled || isExportingCSV}
        >
          <div className="button-content">
            {isExportingCSV ? (
              <div className="spinner-small"></div>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
              </svg>
            )}
            <span>{t('exportCSV')}</span>
          </div>
        </button>
        
        <button 
          className="action-button pdf-button"
          onClick={handleExportPDF}
          disabled={isDisabled || isExportingPDF}
        >
          <div className="button-content">
            {isExportingPDF ? (
              <div className="spinner-small"></div>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                <path d="M20 2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-8.5 7.5c0 .83-.67 1.5-1.5 1.5H9v1.25c0 .41-.34.75-.75.75s-.75-.34-.75-.75V8c0-.55.45-1 1-1H10c.83 0 1.5.67 1.5 1.5v1zm5 2c0 .83-.67 1.5-1.5 1.5h-2c-.28 0-.5-.22-.5-.5v-5c0-.28.22-.5.5-.5h2c.83 0 1.5.67 1.5 1.5v3zm4-3.75c0 .41-.34.75-.75.75H19v1h.75c.41 0 .75.34.75.75s-.34.75-.75.75H19v1.25c0 .41-.34.75-.75.75s-.75-.34-.75-.75V8c0-.55.45-1 1-1h1.25c.41 0 .75.34.75.75zM9 9.5h1v-1H9v1zM3 6c-.55 0-1 .45-1 1v13c0 1.1.9 2 2 2h13c.55 0 1-.45 1-1s-.45-1-1-1H5c-.55 0-1-.45-1-1V7c0-.55-.45-1-1-1zm11 5.5h1v-3h-1v3z"/>
              </svg>
            )}
            <span>{t('exportPDF')}</span>
          </div>
        </button>
      </div>
      
      <button 
        className="action-button share-button"
        onClick={handleShare}
        disabled={isDisabled || isSharing}
      >
        <div className="button-content">
          {isSharing ? (
            <div className="spinner-small"></div>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
              <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z"/>
            </svg>
          )}
          <span>{t('share')}</span>
        </div>
      </button>
    </div>
  );
};

export default ReportActions;